/**
 * The candidate conversation on one Candidate Requests card.
 *
 * ── Why this is a separate file ──────────────────────────────────────────────────────────────
 * Split out of `InboundCard.tsx` when that file hit the 500-line ceiling. The history is also the
 * one part of the card that does its own fetching, so it earns its own component.
 *
 * ── Why it fetches at all ────────────────────────────────────────────────────────────────────
 * The queue embeds only the last few messages per thread (`MAX_HISTORY_MESSAGES` in taInbound.js),
 * because it loads up to 40 threads at once and message bodies are the biggest thing on a mail
 * thread. So the card knows how long the thread REALLY is (`conversationTotal`) but not all of it.
 * Opening the history asks the proxy for the untruncated exchange — one Gmail call, for the one
 * thread being read.
 *
 * Three honesty rules, all of them about not passing off a clipped thread as the whole one:
 *   - the toggle advertises the thread's real length, not the length of the embedded slice
 *   - a failed fetch keeps the slice on screen and points at Gmail; it does not blank the history
 *   - a body that was still cut says so, on the message itself
 */
import { useState } from 'react';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { AttachmentChips } from './AttachmentChips';
import { COLORS } from './format';
import type { InboundMessage, InboundRequest } from '../../types/taActionBoard';

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * One message in the thread.
 *
 * `text` is rendered as TEXT, never HTML — React escapes it, so a candidate's email cannot inject
 * markup or script into the board. That is deliberate and must not be "improved" into
 * dangerouslySetInnerHTML to get formatting back.
 */
function ThreadMessage({
  message,
  threadId,
  gmailUrl,
}: {
  message: InboundMessage;
  threadId: string;
  gmailUrl: string;
}) {
  const outbound = message.direction === 'out';
  return (
    <div className={outbound ? 'pl-6' : ''}>
      <div className="mb-1 flex items-baseline gap-2 text-xs">
        <span className="font-medium text-gray-700">
          {outbound ? `${message.fromName} (us)` : message.fromName}
        </span>
        <span className="text-gray-400">{fmtWhen(message.at)}</span>
      </div>
      <p
        className="whitespace-pre-wrap rounded-lg p-3 text-sm text-gray-700"
        style={{ backgroundColor: outbound ? '#EEF4FC' : COLORS.cotton }}
      >
        {message.text}
        {message.truncated && (
          // Said out loud: a recruiter must never answer half a question without knowing it.
          <span className="mt-2 block text-xs italic text-gray-500">
            … this message is longer — open it in Gmail to read all of it.
          </span>
        )}
      </p>
      {(message.attachments?.length ?? 0) > 0 && (
        <div className="mt-1.5">
          <AttachmentChips threadId={threadId} attachments={message.attachments!} gmailUrl={gmailUrl} />
        </div>
      )}
    </div>
  );
}

export function ThreadHistory({
  item,
  onLoadThread,
}: {
  item: InboundRequest;
  /** Fetches the WHOLE thread. Owned by the tab, which holds the Gmail token and the API. */
  onLoadThread: () => Promise<InboundMessage[]>;
}) {
  const [showThread, setShowThread] = useState(false);
  /**
   * The full thread once fetched. Until then the embedded slice renders immediately, so opening
   * the history is never a blank wait — it fills in and then deepens.
   */
  const [fullThread, setFullThread] = useState<InboundMessage[] | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle');

  // Earlier messages only — the newest one is already shown in full above this component.
  const earlier = (fullThread || item.conversation).filter((m) => m.id !== item.messageId);
  /**
   * What the toggle promises. `conversationTotal` counts the whole thread including the message
   * shown above, so it is the honest figure to advertise; without it (an older proxy) fall back to
   * what we actually hold.
   */
  const earlierTotal = Math.max(
    (item.conversationTotal || item.conversation.length) - 1,
    earlier.length
  );
  /** True while we are still showing less than the thread really has. */
  const partial = !fullThread && earlier.length < earlierTotal;

  if (earlierTotal <= 0) return null;

  const toggle = () => {
    // Counts only: how often the earlier conversation is opened, and how long it is.
    if (!showThread) void logAnalyticsEvent('ta_requests_thread_opened', { earlier_messages: earlierTotal });
    const opening = !showThread;
    setShowThread(opening);
    // Fetch once, on first open. A failure falls back to the embedded slice rather than an error
    // screen — some of the conversation beats none of it.
    if (opening && !fullThread && state !== 'loading') {
      setState('loading');
      onLoadThread()
        .then((messages) => {
          setFullThread(messages);
          setState('idle');
        })
        .catch(() => setState('failed'));
    }
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        className="text-xs text-blue-600 hover:underline"
        onClick={toggle}
        data-no-toggle
      >
        {showThread ? '▾' : '▸'} Earlier in this conversation ({earlierTotal})
      </button>
      {showThread && (
        <div className="mt-2 max-h-96 space-y-3 overflow-y-auto pr-1">
          {state === 'loading' && partial && (
            <p className="text-xs text-gray-400">Loading the rest of the conversation…</p>
          )}
          {state === 'failed' && partial && (
            // Say which part is missing rather than failing the whole history.
            <p className="text-xs text-gray-500">
              Could not load the older messages —{' '}
              <a
                href={item.gmailUrl}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                open the thread in Gmail ↗
              </a>
            </p>
          )}
          {earlier.map((m) => (
            <ThreadMessage key={m.id} message={m} threadId={item.threadId} gmailUrl={item.gmailUrl} />
          ))}
        </div>
      )}
    </div>
  );
}
